import React from 'react';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { AssetTreeView } from '../../../types/app/assetTreeView.types';
import { dateShowFormat, utcFormat } from '../../../types/core/date.types';
import * as machineDataApi from '../../../api/app/machineDataApi';
import * as assetApi from '../../../api/app/assetApi';
import * as commentApi from '../../../api/app/performanceCommentApi';
import { Loader } from '../../../components/core/Loader/Loader';
import { generateParentNames } from '../../../utils/core/functions/assetTreeView.fuctions';
import {
  checkAndFillMissingValues,
  getValByGranularity,
  prepareDataForExport,
  testDataProcess,
} from '../../../utils/core/functions/dataProcess.functions';
import { convertGranularityToMinutes } from '../../../utils/core/functions/convertDateTime';
import { Timezone } from '../../../components/app/TimezoneChanger/TimezoneChanger';
import { DashboardTable } from '../../../components/app/DashboardTable/DashboardTable';
import { dateTimeShowFormat, hoursMinutesShowFormat } from '../../../types/core/date.types';
import { ChartTypes, ChartDataSet } from '../../../types/app/chart.types';
import { ChartsContainerHistory } from '../../Charts';
import { Button, Grid, Typography } from '@mui/material';
import {
  BLUE,
  DARK_BLUE,
  getColorForColumn,
  getColorForHistoryColumn,
  YELLOW,
} from '../../../utils/core/functions/colors.function';
import { useTranslation } from '../../../contexts/translation';
import { useStyles } from './DashboardPerformanceHistory.styles';
import { Asset, AssetDashboardSetting } from '../../../types/app/asset.types';
import { InteractionItem } from 'chart.js';
import { PerformanceCommentModal } from '../../../components/app/PerformanceComment/PerformanceComment';
import { ThemeType, useLocalTheme } from '../../../hooks/useLocalTheme';

interface Props {
  dateFrom: Moment;
  dateTo: Moment;
  assetTreeViewData: AssetTreeView;
  selectedAssets: AssetTreeView[];
  timezone: Timezone;
  granularity: string;
  settingData: AssetDashboardSetting;
  selectedAssetSettingData?: AssetDashboardSetting;
}

interface CountersScrapsPlanComments {
  counters: any[];
  scraps: any[];
  plan: any[];
  comments: any[];
}

export function DashboardPerformanceHistoryDataLoader({
  dateFrom,
  dateTo,
  assetTreeViewData,
  selectedAssets,
  timezone,
  granularity,
  settingData,
  selectedAssetSettingData,
}: Props) {
  const { localTheme } = useLocalTheme();
  const { t, lang } = useTranslation();
  const classes = useStyles();
  const [assetTreeViewDataCustom, setAssetTreeViewDataCustom] = useState<AssetTreeView | null>();
  const [countersScrapsPlanComments, setCountersScrapsPlanComments] =
    useState<CountersScrapsPlanComments | null>(null);
  const [dataComponentCounters, setDataComponentCounters] = useState<'chart' | 'table'>('table');
  const [dataComponentScraps, setDataComponentScraps] = useState<'chart' | 'table'>('chart');
  const [proccessedData, setProccessedData] = useState<any>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [refetch, setRefetch] = useState<boolean>(true);
  const [processDataObject, setProcessDataObject] = useState<any>();

  // Rekurzivně doplníme parent names do stromu assetů
  useEffect(() => {
    if (!assetTreeViewData) return;
    setAssetTreeViewDataCustom(generateParentNames(assetTreeViewData, ''));
  }, [assetTreeViewData]);

  // Při změně filtru (assetu nebo datumu), se projde celé asset tree view, a pro každý asset se načtou datasety
  useEffect(() => {
    if (!selectedAssets || !dateFrom || !dateTo || !refetch) return;
    setLoading(true);

    const getAndSetCountersScrapsForAssetTreeView = async () => {
      const resultsCounters = await Promise.all(
        selectedAssets.map(async (asset) => {
          let counter = await machineDataApi.readCounterQtyFilterByDate({
            machine: asset.influxMachineName,
            dateFrom: dateFrom.clone().format(utcFormat),
            dateTo: dateTo.clone().format(utcFormat),
            // influx vrací špatně posunuté hodnoty pro granularitu 2h a víc => posílat 1h a pak přepočítat
            aggregation: convertGranularityToMinutes(granularity) > 60 ? '1h' : granularity,
          });
          // přepočet hodnot v případě vyšší granularity než 1h
          console.log(counter.data, 'counter.data');
          if (convertGranularityToMinutes(granularity) > 60 && counter.data.length > 0)
            counter.data.map(
              (counter: any) =>
                (counter.values = getValByGranularity(
                  checkAndFillMissingValues(counter.values, dateFrom, dateTo),
                  convertGranularityToMinutes(granularity),
                  timezone,
                )),
            );
          counter = {
            ...counter,
            id: asset.id,
          };
          return counter;
        }),
      );

      const resultsScraps = await Promise.all(
        selectedAssets.map(async (asset) => {
          let scrap = await machineDataApi.readScrapFilterByDate({
            machine: asset.influxMachineName,
            dateFrom: dateFrom.clone().format(utcFormat),
            dateTo: dateTo.clone().format(utcFormat),
            // influx vrací špatně posunuté hodnoty pro granularitu 2h a víc => posílat 1h a pak přepočítat
            aggregation: convertGranularityToMinutes(granularity) > 60 ? '1h' : granularity,
          });
          // přepočet hodnot v případě vyšší granularity než 1h
          if (convertGranularityToMinutes(granularity) > 60 && scrap.data.length > 0)
            scrap.data.map(
              (scrap: any) =>
                (scrap.values = getValByGranularity(
                  checkAndFillMissingValues(scrap.values, dateFrom, dateTo),
                  convertGranularityToMinutes(granularity),
                  timezone,
                )),
            );
          scrap = { ...scrap, id: asset.id };
          return scrap;
        }),
      );

      const resultsPlan = await Promise.all(
        selectedAssets.map(async (asset) => {
          const plan = await assetApi.getAssetPlannedQtyForAsset({
            id: asset.id,
            dateFrom: dateFrom.clone().format(utcFormat),
            dateTo: dateTo.clone().format(utcFormat),
          });
          return plan;
        }),
      );

      const resultsComments = await Promise.all(
        selectedAssets.map(async (asset) => {
          let comments = await commentApi.getListFiltered({
            assetId: asset.id,
            from: dateFrom.clone().format(utcFormat),
            to: dateTo.clone().format(utcFormat),
          });
          return comments;
        }),
      );

      setCountersScrapsPlanComments({
        counters: resultsCounters,
        scraps: resultsScraps,
        plan: resultsPlan,
        comments: resultsComments,
      });
    };
    getAndSetCountersScrapsForAssetTreeView();
  }, [selectedAssets, dateFrom, dateTo, refetch]);

  useEffect(() => {
    if (
      !countersScrapsPlanComments ||
      countersScrapsPlanComments.counters.length === 0 ||
      countersScrapsPlanComments.scraps.length === 0
    ) {
      setProccessedData(null);
      return;
    }

    let data: any[] = [];
    let configs: any[] = [];
    countersScrapsPlanComments.scraps.map((scrap) => {
      scrap.data.map((dataSet: any) => {
        const iFrom = data[data.length - 1]
          ? data[data.length - 1].length + configs[data.length - 1].iFrom
          : 0;
        const iTo = iFrom + dataSet.values.length - 1;
        configs = [
          ...configs,
          {
            iFrom: iFrom,
            iTo: iTo,
            dataSetId: dataSet.dataId + '_' + scrap.id,
            dataSetValueKey: 'value',
          },
        ];
        data = [...data, dataSet.values];
      });
    });

    countersScrapsPlanComments.counters.map((counter) => {
      counter.data.map((dataSet: any) => {
        const iFrom = data[data.length - 1]
          ? data[data.length - 1].length + configs[data.length - 1].iFrom
          : 0;
        const iTo = iFrom + dataSet.values.length - 1;
        configs = [
          ...configs,
          {
            iFrom: iFrom,
            iTo: iTo,
            dataSetId: dataSet.dataId + '_' + counter.id,
            dataSetValueKey: 'value',
          },
        ];
        data = [...data, dataSet.values];
      });
    });

    countersScrapsPlanComments.plan.map((plan, i) => {
      if (plan.length > 0) {
        const iFrom = data[data.length - 1]
          ? data[data.length - 1].length + configs[data.length - 1].iFrom
          : 0;
        const iTo = iFrom + plan.length - 1;

        configs = [
          ...configs,
          {
            iFrom: iFrom,
            iTo: iTo,
            dataSetId: `plan_${i}`,
            dataSetValueKey: 'value',
          },
        ];

        let planData: any = [];
        plan.map((dataSet: any) => {
          planData = [
            ...planData,
            {
              time: dataSet.time,
              value: dataSet.qty,
            },
          ];
        });
        data = [...data, planData];
      }
    });

    // komentáře
    const iFrom = data[data.length - 1]
      ? data[data.length - 1].length + configs[data.length - 1].iFrom
      : 0;

    const iTo = iFrom + data[0]?.iTo;
    configs = [
      ...configs,
      {
        iFrom: iFrom,
        iTo: iTo,
        dataSetId: 'IS_COMMENT',
        dataSetValueKey: 'value',
      },
    ];

    const _proccessedData = testDataProcess({
      title: '',
      data: [].concat(...data),
      intervals: [
        {
          start: dateFrom.clone().add(convertGranularityToMinutes(granularity), 'minutes'),
          end: dateTo.clone(),
        },
      ],
      configs: configs,
      chartsConfig: [],
      granularity: convertGranularityToMinutes(granularity),
      timezone: timezone.value,
    });
    setProccessedData(_proccessedData);
    setLoading(false);
  }, [countersScrapsPlanComments, lang]);

  // Tabulkový pohled na counters
  const datboardTableCounters = useMemo(() => {
    if (isLoading) return <Loader />;
    if (!proccessedData || dataComponentCounters === 'chart') return;

    // Pokud se první a poslední čas nachází ve stejném dni, chceme zobrazit jen čas. Pokud mají odlišný den, zobrazíme i datum
    const isSameDay = proccessedData[0].timeObject.isSame(
      proccessedData[proccessedData.length - 1].timeObject.clone().add(-1, 'seconds'),
      'day',
    );
    const minToSubstract = -1 * convertGranularityToMinutes(granularity);
    const headers = proccessedData.map((item: any) => {
      const dateFrom = item
        .timeObject!.clone()
        .add(minToSubstract, 'minutes')
        .tz(timezone.value)
        .format(
          granularity === '24h'
            ? dateShowFormat
            : isSameDay
            ? hoursMinutesShowFormat
            : dateTimeShowFormat,
        );
      return {
        label: dateFrom,
        tooltip:
          dateFrom + ' - ' + item.timeObject!.tz(timezone.value).format(hoursMinutesShowFormat),
      };
    });

    let _rows: any[] = [];
    // Plány
    _rows = [
      ..._rows,
      ...selectedAssets.map((asset, i) => {
        let rowSum = 0;
        return {
          label: `${asset.name} - ` + t('page.dashboard.graph-dataset-label-plan'),
          data: proccessedData.map((value: any) => {
            value?.values.map((val: any) => {
              if (val.id === `plan_${i}`) {
                rowSum = rowSum + val.value;
              }
            });
            return value.values.filter((x: any) => x.id === `plan_${i}`)[0]?.value;
          }),
          calculateColumnSum: false,
          sum: rowSum,
          style: {
            fontWeight: 'bold',
            color:
              localTheme === ThemeType.DARK
                ? `rgb(${DARK_BLUE.r},${DARK_BLUE.g},${DARK_BLUE.b})`
                : `rgb(${BLUE.r},${BLUE.g},${BLUE.b})`,
          },
          isPlan: true,
        };
      }),
    ];

    // zobrazit jen plány, kde nejsou všude undefined hodnoty
    let rows: any[] = [];
    _rows.map((row: any) => {
      if (row.data.some((val: any) => val !== undefined)) rows = [...rows, row];
    });

    // Counters
    selectedAssets.forEach((asset) => {
      rows = [
        ...rows,
        ...asset.counters.map((item) => {
          let rowSum = 0;
          return {
            label: `${item.parentAssetName} - ${item.name}`,
            data: proccessedData.map((value: any) => {
              value?.values.map((val: any) => {
                if (val.id === item.influxValueName + '_' + asset.id) {
                  rowSum = rowSum + val.value;
                }
              });
              return value.values.filter(
                (x: any) => x.id === item.influxValueName + '_' + asset.id,
              )[0]?.value;
            }),
            calculateColumnSum: true,
            sum: rowSum,
            style: { '&:last-child': { fontWeight: 'bold' } },
          };
        }),
      ];
    });

    // Do rows se přidá nový řádek, ve kterém budou sumy sloupců.
    let sumOfColls: any = [];
    let sumOfSums: number = 0;
    for (let i = 0; i < proccessedData.length; i++) {
      let colSum = 0;
      rows
        .filter((x) => x.calculateColumnSum)
        .forEach((row) => {
          colSum += row.data[i] === undefined ? 0 : row.data[i];
        });
      sumOfSums += colSum;
      sumOfColls = [...sumOfColls, colSum];
    }
    rows = [
      ...rows,
      {
        label: '',
        data: sumOfColls,
        sum: sumOfSums,
        style: { fontWeight: 'bold' },
      },
    ];

    if (rows.length === 0) return <>{t('general.messages.error.no-data-to-display')}</>;
    return (
      <DashboardTable
        headers={headers}
        items={rows}
        isSameDay={isSameDay}
        exportOptions={{
          fileName: 'Counter-performance',
          data: prepareDataForExport(rows, headers),
        }}
      />
    );
  }, [proccessedData, dataComponentCounters, isLoading]);

  // Tabulkový pohled na scraps
  const datboardTableScraps = useMemo(() => {
    if (isLoading) return <Loader />;
    if (!proccessedData || dataComponentScraps === 'chart') return;

    const isSameDay = proccessedData[0].timeObject.isSame(
      proccessedData[proccessedData.length - 1].timeObject.clone().add(-1, 'seconds'),
      'day',
    );

    const minToSubstract = -1 * convertGranularityToMinutes(granularity);
    const headers = proccessedData.map((item: any) => {
      const dateFrom = item
        .timeObject!.clone()
        .add(minToSubstract, 'minutes')
        .tz(timezone.value)
        .format(
          granularity === '24h'
            ? dateShowFormat
            : isSameDay
            ? hoursMinutesShowFormat
            : dateTimeShowFormat,
        );
      return {
        label: dateFrom,
        tooltip:
          dateFrom + ' - ' + item.timeObject!.tz(timezone.value).format(hoursMinutesShowFormat),
      };
    });

    let rows: any[] = [];
    selectedAssets.forEach((asset) => {
      rows = [
        ...rows,
        ...asset.countersScrap.map((item) => {
          let rowSum = 0;
          return {
            label: `${item.parentAssetName} - ${item.name}`,
            data: proccessedData.map((value: any) => {
              value?.values.map((val: any) => {
                if (val.id === item.influxValueName + '_' + asset.id) {
                  rowSum = rowSum + val.value;
                }
              });
              return value.values.filter(
                (x: any) => x.id === item.influxValueName + '_' + asset.id,
              )[0]?.value;
            }),
            sum: rowSum,
            style: { '&:last-child': { fontWeight: 'bold' } },
          };
        }),
      ];
    });

    let sumOfColls: any = [];
    let sumOfSums: number = 0;
    for (let i = 0; i < proccessedData.length; i++) {
      let colSum = 0;
      rows.forEach((row) => {
        colSum += row.data[i] === undefined ? 0 : row.data[i];
      });
      sumOfSums += colSum;
      sumOfColls = [...sumOfColls, colSum];
    }
    rows = [
      ...rows,
      {
        label: '',
        data: sumOfColls,
        sum: sumOfSums,
        style: { fontWeight: 'bold' },
      },
    ];
    if (rows.length === 0) return <>{t('general.messages.error.no-data-to-display')}</>;

    return (
      <DashboardTable
        headers={headers}
        items={rows}
        isSameDay={isSameDay}
        exportOptions={{
          fileName: 'Scrap-performance',
          data: prepareDataForExport(rows, headers),
        }}
      />
    );
  }, [proccessedData, dataComponentScraps, isLoading]);

  // Graf counters
  const dashboardChartCounters = useMemo(() => {
    if (isLoading) return <Loader />;
    if (!proccessedData || dataComponentCounters === 'table') return;
    const isSameDay = proccessedData[0].timeObject.isSame(
      proccessedData[proccessedData.length - 1].timeObject.clone().add(-1, 'seconds'),
      'day',
    );
    const labels = proccessedData.map((item: any) => {
      return item
        .timeObject!.tz(timezone.value)
        .format(
          granularity === '24h'
            ? dateShowFormat
            : isSameDay
            ? hoursMinutesShowFormat
            : dateTimeShowFormat,
        );
    });

    let _chartsConfig: any = [];
    let chartsConfig: any = [];
    // Plány
    _chartsConfig = [
      ..._chartsConfig,
      ...selectedAssets.map((asset, i) => {
        return {
          label: `${asset.name} - ` + t('page.dashboard.graph-dataset-label-plan'),
          dataSetId: `plan_${i}`,
          type: ChartTypes.LINE,
          style: {
            borderColor:
              localTheme === ThemeType.DARK
                ? `rgb(${DARK_BLUE.r},${DARK_BLUE.g},${DARK_BLUE.b})`
                : `rgb(${BLUE.r},${BLUE.g},${BLUE.b})`,
            borderWidth: 2,
            fill: false,
          },
          isPlan: true,
        };
      }),
    ];
    _chartsConfig.map((config: any) => {
      if (proccessedData[0].values.some((val: any) => val.id === config.dataSetId)) {
        chartsConfig = [...chartsConfig, config];
      }
    });

    // Komentáře
    chartsConfig = [
      ...chartsConfig,
      {
        dataSetId: 'IS_COMMENT',
        type: ChartTypes.LINE,
        label: t('page.dashboard.graph-dataset-label-comments'),
        style: {
          borderColor: `rgb(${YELLOW.r},${YELLOW.g},${YELLOW.b})`,
          borderWidth: 8,
          fill: false,
          showLine: false,
          pointStyle: 'triangle',
        },
      },
    ];

    // Countery
    let isGreen = true;
    let isBlue = false;
    let colorCounter = 0;
    selectedAssets.forEach((asset) => {
      chartsConfig = [
        ...chartsConfig,
        ...asset.counters.map((item) => {
          const r = 0;
          let g = 255;
          let b = 0;

          if (isGreen && !isBlue) g = 255 - 30 * colorCounter;
          else if (isGreen && isBlue) {
            g = 255 - 30 * colorCounter;
            b = 255 - 30 * colorCounter;
          } else {
            g = 0;
            if (!isBlue) {
              colorCounter = 0;
              isBlue = true;
            }
            b = 255 - 30 * colorCounter;
          }

          if (g < 50) isGreen = false;
          colorCounter++;
          return {
            dataSetId: item.influxValueName + '_' + asset.id,
            label: `${item.parentAssetName} - ${item.name}`,
            type: ChartTypes.BAR,
            style: {
              backgroundColor: {
                r: r,
                g: localTheme === ThemeType.DARK ? g - 80 : g,
                b: b,
              },
              borderWidth: 0.1,
              fill: true,
            },
            assetId: asset.id,
          };
        }),
      ];
    });

    let chartsData: any[] = [];
    let counterCount = 0;
    let planCount = 0;
    chartsConfig.map((c: any) => {
      c.dataSetId.includes('qty') ? counterCount++ : null;
      c.dataSetId.includes('plan') ? planCount++ : null;
    });
    chartsData = chartsConfig.map((config: any) => {
      let sum = 0;
      let showSum = true;
      return {
        ...config,
        dataSetId: config.dataSetId,
        chartType: config.type,
        data: [
          ...proccessedData.map((item: any) => {
            const datasetValue = item.values.filter((x: any) => x.id === config.dataSetId)[0];
            // dataset Comments
            let isComment = false;
            if (config.dataSetId === 'IS_COMMENT' && countersScrapsPlanComments?.comments) {
              showSum = false;
              countersScrapsPlanComments?.comments.forEach((assetComment: any) =>
                assetComment.some((comment: any) => {
                  comment.time === item.time ? (isComment = true) : '';
                }),
              );
              if (isComment) {
                let maxVal = 0;
                item.values.map((x: any) => {
                  x.value > maxVal && x.id.includes('qty') ? (maxVal = x.value) : '';
                });
                return maxVal + 100;
              } else {
                return null;
              }
            }
            if (datasetValue && !isComment) {
              sum += datasetValue.value;
              return datasetValue.value;
            }
          }),
        ],
        colors: [
          ...proccessedData.map((item: any) => {
            if (planCount === 1 && counterCount === 1) {
              const value = item.values.filter((x: any) => x.id === config.dataSetId)[0]?.value;
              const plan = item.values.filter((x: any) => x.id.includes('plan'))[0]?.value;
              return getColorForHistoryColumn(
                value,
                plan,
                selectedAssetSettingData
                  ? selectedAssetSettingData.qtyLevelOrange
                  : settingData.qtyLevelOrange,
                selectedAssetSettingData
                  ? selectedAssetSettingData.qtyLevelGreen
                  : settingData.qtyLevelGreen,
                localTheme,
              );
            }
            if (config.style.backgroundColor) return config.style.backgroundColor;
            if (config.type === ChartTypes.LINE && config.dataSetId === 'IS_COMMENT') return YELLOW;
            if (config.type === ChartTypes.LINE) return BLUE;
          }),
        ],
        sum: sum,
        showSum: showSum,
        isPlan: config.isPlan,
        assetId: config.assetId,
      };
    });
    const chartsDataSets: ChartDataSet[] = chartsData.map((item) => {
      return {
        chartType: item.chartType,
        style: item.style,
        label: item.label,
        colors: item.colors,
        x: labels,
        y: item.data,
        isPlan: item.isPlan,
        assetId: item.assetId ? item.assetId : null,
        dataSetId: item.dataSetId,
        showSum: item.showSum,
        sum: item.sum,
      };
    });

    const onChartClick = (result: InteractionItem[]) => {
      if (result && result.length > 0) {
        let tmpDataset = chartsDataSets[result[0].datasetIndex];
        // otevřít modal jen při kliku na trojúhelník
        if (tmpDataset.dataSetId === 'IS_COMMENT') {
          // najít asset podle kliknutého sloupce (counteru)
          // let tmpId = chartsDataSets[result[0].datasetIndex].assetId;
          // let assetTreeItem = selectedAssets.find((x: any) => x.id === tmpId);
          // setClickedAsset({ id: assetTreeItem?.id, name: assetTreeItem?.name } as Asset);
          setProcessDataObject(proccessedData[result[0].index]);
          setOpen(true);
        }
      }
    };
    if (chartsDataSets.length === 0) return <>{t('general.messages.error.no-data-to-display')}</>;
    return (
      <ChartsContainerHistory
        chartData={chartsDataSets}
        labels={labels}
        chartHeight={'300px'}
        chartTitle={t('page.dashboard.history.performance.chart-title-1')}
        onClick={onChartClick}
        localTheme={localTheme}
      />
    );
  }, [proccessedData, dataComponentCounters, isLoading]);

  // Graf scraps
  const dashboardChartScraps = useMemo(() => {
    if (isLoading) return <Loader />;
    if (!proccessedData || dataComponentScraps === 'table') return;

    let isSameDay = proccessedData[0].timeObject.isSame(
      proccessedData[proccessedData.length - 1].timeObject.clone().add(-1, 'seconds'),
      'day',
    );

    const labels = proccessedData.map((item: any) => {
      return item
        .timeObject!.tz(timezone.value)
        .format(
          granularity === '24h'
            ? dateShowFormat
            : isSameDay
            ? hoursMinutesShowFormat
            : dateTimeShowFormat,
        );
    });

    let chartsConfig: any = [];
    let isRed = true;
    let isGreen = false;
    let isBlue = false;
    let colorCounter = 0;
    selectedAssets.forEach((asset) => {
      chartsConfig = [
        ...chartsConfig,
        ...asset.countersScrap.map((item) => {
          let r = 255;
          let g = 0;
          let b = 0;

          if (isRed && !isBlue) r = 255 - 60 * colorCounter;
          else if (isRed && isBlue) {
            r = 255 - 60 * colorCounter;
            b = 255 - 60 * colorCounter;
          } else {
            r = 100;
            if (!isBlue) {
              colorCounter = 0;
              isBlue = true;
            }
            b = 255 - 60 * colorCounter;
          }

          if (isBlue && b < 50) {
            isBlue = false;
            isGreen = true;
            colorCounter = 0;
          }

          if (isGreen) {
            r = 150;
            g = 120 - 60 * colorCounter;
          }
          if (isGreen && g < 30) {
            colorCounter = 0;
            isBlue = true;
            isGreen = false;
          }
          if (r < 50) isRed = false;
          colorCounter++;

          return {
            dataSetId: item.influxValueName + '_' + asset.id,
            label: `${item.parentAssetName} - ${item.name}`,
            type: ChartTypes.BAR,
            style: {
              backgroundColor: {
                r: localTheme === ThemeType.DARK ? r - 30 : r,
                g: g,
                b: b,
              },
              borderWidth: 0.1,
              fill: true,
            },
          };
        }),
      ];
    });

    let chartsData: any[] = [];
    chartsData = chartsConfig!.map((config: any) => {
      let sum = 0;
      let showSum = true;
      return {
        ...config,
        dataSetId: config.dataSetId,
        chartType: config.type,
        data: [
          ...proccessedData.map((item: any) => {
            const datasetValue = item.values.filter((x: any) => x.id === config.dataSetId)[0];
            if (datasetValue) {
              sum += datasetValue.value;
              return datasetValue.value;
            }
          }),
        ],
        colors: [
          ...proccessedData.map((item: any) => {
            if (config.style.backgroundColor) return config.style.backgroundColor;
            if (config.type === ChartTypes.LINE)
              return localTheme === ThemeType.DARK ? DARK_BLUE : BLUE;
            const value = item.values.filter((x: any) => x.id === config.dataSetId)[0].value;
            return 1300 > value ? { r: 255, g: 0, b: 0 } : { r: 10, g: 230, b: 0 };
          }),
        ],
        sum: sum,
        showSum: showSum,
      };
    });

    const chartsDataSets: ChartDataSet[] = chartsData.map((item) => {
      return {
        chartType: item.chartType,
        style: item.style,
        label: item.label,
        colors: item.colors,
        x: labels,
        y: item.data,
        showSum: item.showSum,
        sum: item.sum,
      };
    });

    if (chartsDataSets.length === 0) return <>{t('general.messages.error.no-data-to-display')}</>;

    return (
      <ChartsContainerHistory
        chartData={chartsDataSets}
        labels={labels}
        chartHeight={'300px'}
        chartTitle={t('page.dashboard.history.performance.chart-title-2')}
        localTheme={localTheme}
      />
    );
  }, [proccessedData, dataComponentScraps, isLoading]);

  if (!assetTreeViewDataCustom || !countersScrapsPlanComments) return <Loader />;

  if (selectedAssets.length === 0)
    return <Typography variant={'h5'}>{t('page.dashboard.history.dataset-no-asset')}</Typography>;

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant={'h5'} className={classes.datasetHeadline}>
          {t('page.dashboard.history.performance.dataset-headline-1')}
        </Typography>
        <Button
          onClick={() =>
            setDataComponentCounters(dataComponentCounters === 'table' ? 'chart' : 'table')
          }
        >
          {dataComponentCounters === 'table'
            ? t('page.dashboard.history.dataset-view-switch-chart')
            : t('page.dashboard.history.dataset-view-switch-table')}
        </Button>
      </Grid>
      <Grid item xs={12}>
        {dataComponentCounters === 'table' ? datboardTableCounters : dashboardChartCounters}
      </Grid>
      <Grid item xs={12} mt={2}>
        <Typography variant={'h5'} className={classes.datasetHeadline}>
          {t('page.dashboard.history.performance.dataset-headline-2')}
        </Typography>
        <Button
          onClick={() =>
            setDataComponentScraps(dataComponentScraps === 'table' ? 'chart' : 'table')
          }
        >
          {dataComponentScraps === 'table'
            ? t('page.dashboard.history.dataset-view-switch-chart')
            : t('page.dashboard.history.dataset-view-switch-table')}
        </Button>
      </Grid>
      <Grid item xs={12}>
        {dataComponentScraps === 'table' ? datboardTableScraps : dashboardChartScraps}
      </Grid>
      <PerformanceCommentModal
        open={open}
        setOpen={setOpen}
        processDataObject={processDataObject}
        // veškeré komentáře jen na hlavní asset
        asset={{ id: assetTreeViewData.id, name: assetTreeViewData.name } as Asset}
        granularity={convertGranularityToMinutes(granularity)}
        timezone={timezone.value}
        setRefetch={setRefetch}
      />
    </Grid>
  );
}
